import { useState } from "react";
import { Box, Button, Center, Divider, Flex, Grid, HStack, Heading, IconButton, Stack, Tag, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useAuth, WithAuthorization } from "../../context";
import {
  supplierQuestionnaireKeys,
  useGetSupplierQuestionnaireById,
  useRevertQuestionnaireCategory,
  useSetQuestionnaireCategory,
  useUnfinaliseSupplierQuestionnaire,
} from "../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import {
  QUESTIONNAIRE_STATUSES,
  showErrorToast,
  showSuccessToast,
  SUPPLIER_QUESTIONNAIRE_TYPES,
  SUPPLIER_TYPES,
  USER_ROLES,
} from "../../utils";
import { isAllowedToView } from "utils/AuthUtils";
import { SelectCategory } from "./components/SelectCategory";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "./helpers";
import { Product } from "./components/Product";
import { ConsumptionBasedRevenue, Revenue } from "./components/Revenue";
import { Location } from "./components/Location";
import { useLocation, useNavigate } from "react-router-dom";
import * as React from "react";
import { CalendarBlank, CaretLeft, Notepad, User } from "@phosphor-icons/react";
import { FaArrowLeft } from "react-icons/fa";
import { getSupplierQuestionnairesRoute } from "../SupplierQuestionnaires/helpers";
import { routes } from "../../config";
import { DecarbonizationNetZeroPlan } from "./components/DecarbonizationNetZeroPlan";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { useQueryClient } from "react-query";
import { useRightSidebar } from "../../context/RightSidebarContext";
import { ManagePanel } from "./components/ManagePanel";

function SupplierQuestionnaire() {
  const { t } = useTranslation("translation");
  const location = useLocation();
  const toast = useToast();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userPermissions } = useAuth();
  const queryClient = useQueryClient();
  const assignee = {
    name: user.name,
    avatarUrl: user.picture,
  };
  const [questionnaireData, setQuestionnaireData] = useState();
  const [submitAndReturnToList, setSubmitAndReturnToList] = useState(false);

  const { isOpen: isGetBackDialogOpen, onOpen: onGetBackDialogOpen, onClose: onGetBackDialogClose } = useDisclosure();

  const TASK_TRAY_ID = "SupplierQuestionnaireTaskDetails";
  const { setRightSidebar, doOpen, doClose, isOpen: isSidebarOpen, activationId } = useRightSidebar();
  const isTaskDetailsOpen = activationId === TASK_TRAY_ID && isSidebarOpen;

  const { mutate: unfinaliseQuestionnaire } = useUnfinaliseSupplierQuestionnaire({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, t("messages.unfinaliseSuccess"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleUnfinaliseTask = (id) => {
    unfinaliseQuestionnaire({ questionnaireId: id, type: questionnaireData?.questionnaireType });
  };

  const { data, isLoading, isFetching, error } = useGetSupplierQuestionnaireById(
    location.search.split("id=")[1] ?? null,
    {
      enabled:
        location &&
        location.search !== "" &&
        !isNaN(location.search.split("id=")[1]) &&
        location.search.split("id=")[1] !== null,
      onSuccess: (response) => {
        setQuestionnaireData(response);
      },
      onError: (err) => (err.response?.status !== 404 ? showErrorToast(toast, err.message) : null),
      onSettled: () => dispatch(hideAppSpinner()),
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const executeNavigation = () => {
    // If we arrived from a specific page (e.g. a supplier's detail page in
    // Supply Chain), "Back to tasks" should return there rather than always
    // going to the generic Supplier Questionnaires / Data entry planning pages.
    if (location.state?.from) {
      navigate(location.state.from, { state: location.state.fromState });
      return;
    }

    if (
      assignee?.name?.toLowerCase() === questionnaireData?.assigneeName?.toLowerCase() ||
      (assignee?.name?.toLowerCase() !== questionnaireData?.assigneeName?.toLowerCase() &&
        !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
      (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER))
    ) {
      // The assignee of the questionnaire is editing it or colleague of the assignee is reviewing it, so we are
      // returning to 'Supplier Questionnaires' page
      navigate(getSupplierQuestionnairesRoute(userPermissions));
    } else {
      // Admin is editing someone else's questionnaire, so we are returning to 'Data entry planning' page
      navigate(routes.supplierPortalDataEntryPlanning);
    }
  };

  const handleReturnToListClick = () => {
    if (
      questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED ||
      (questionnaireData.assigneeName?.toLowerCase() !== user.email?.toLowerCase() &&
        !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
      (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER)) ||
      !questionnaireData.category ||
      (questionnaireData.category === SUPPLIER_QUESTIONNAIRE_CATEGORIES.PRODUCT &&
        questionnaireData.products?.length === 0) ||
      (questionnaireData.category === SUPPLIER_QUESTIONNAIRE_CATEGORIES.LOCATION &&
        questionnaireData.locations?.length === 0)
    ) {
      executeNavigation();
    } else {
      setSubmitAndReturnToList(true);
    }
  };

  const handleNavigationToList = () => {
    setSubmitAndReturnToList(false);
    executeNavigation();
  };

  const { mutate: setCategory } = useSetQuestionnaireCategory({
    onSuccess: (response) => {
      setQuestionnaireData(response);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleSetCategory = (selectedCategory) => {
    if (selectedCategory !== null) {
      dispatch(showAppSpinner());
      setCategory({ id: questionnaireData.id, category: selectedCategory });
    }
  };

  const { mutate: revertCategoryChoice } = useRevertQuestionnaireCategory({
    onSuccess: () => {
      onGetBackDialogClose();
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleCategorySwitch = () => {
    revertCategoryChoice({ id: questionnaireData?.id, category: questionnaireData?.category });
  };

  const renderCategoryComponent = () => {
    const isPreview =
      questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED ||
      (questionnaireData.assigneeName?.toLowerCase() !== user.email?.toLowerCase() &&
        !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
      (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER));
    let categoryComponent;
    let translationPath = "";

    if (questionnaireData.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN) {
      translationPath = "supplierQuestionnaire.decarbonizationNetZeroPlan";
      categoryComponent = (
        <DecarbonizationNetZeroPlan
          data={questionnaireData}
          isPreview={isPreview}
          returnToListRequested={submitAndReturnToList}
          declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
          returnToList={handleNavigationToList}
        />
      );
    } else {
      switch (questionnaireData.category) {
        case SUPPLIER_QUESTIONNAIRE_CATEGORIES["PRODUCT"]:
          translationPath = "supplierQuestionnaire.product";
          categoryComponent = (
            <Product
              data={questionnaireData}
              returnToListRequested={submitAndReturnToList}
              declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
              returnToList={handleNavigationToList}
              isPreview={isPreview}
            />
          );
          break;
        case SUPPLIER_QUESTIONNAIRE_CATEGORIES["REVENUE"]:
          let revenueType = "";
          if (questionnaireData.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED) {
            revenueType = "emissionBasedRevenue";
            categoryComponent = (
              <Revenue
                data={{ ...questionnaireData, ...questionnaireData.revenue }}
                submitAndReturnToList={submitAndReturnToList}
                setSubmitAndReturnToList={setSubmitAndReturnToList}
                handleNavigationToList={handleNavigationToList}
                isPreview={isPreview}
              />
            );
          } else if (questionnaireData.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED) {
            revenueType = "consumptionBasedRevenue";
            categoryComponent = (
              <ConsumptionBasedRevenue
                data={{ ...questionnaireData, ...questionnaireData.revenue }}
                submitAndReturnToList={submitAndReturnToList}
                setSubmitAndReturnToList={setSubmitAndReturnToList}
                handleNavigationToList={handleNavigationToList}
                isPreview={isPreview}
              />
            );
          }
          translationPath = `supplierQuestionnaire.${revenueType}`;
          break;
        case SUPPLIER_QUESTIONNAIRE_CATEGORIES["LOCATION"]:
          translationPath = "supplierQuestionnaire.location";
          categoryComponent = (
            <Location
              data={questionnaireData}
              returnToListRequested={submitAndReturnToList}
              declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
              returnToList={handleNavigationToList}
              isPreview={isPreview}
            />
          );
          break;
        default:
          translationPath = "supplierQuestionnaire.selectCategory";
          categoryComponent =
            (questionnaireData.assigneeName?.toLowerCase() !== user.email?.toLowerCase() &&
              !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
            (questionnaireData.assigneeName?.toLowerCase() === user.email?.toLowerCase() &&
              userPermissions?.length === 1 &&
              userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER)) ? (
              <Text fontSize="lg" fontWeight="bold">
                {t("reportsPage.typeNotSelected")}
              </Text>
            ) : (
              <SelectCategory handleSetCategory={handleSetCategory} />
            );
      }
    }

    const showGetBackButton =
      // Questionnaire type must be Emission Based
      questionnaireData?.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED &&
      // Button must be visible only for the assignee (Supplier Editor) and the admins (Org Admin and Supplier Admin)
      ((questionnaireData?.assigneeName?.toLowerCase() === user.email?.toLowerCase() &&
        userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER)) ||
        isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) &&
      // Button must be only shown if the category is already selected
      typeof questionnaireData?.category !== "undefined" &&
      questionnaireData?.category !== null;

    return (
      <Flex direction="column" w="100%" minH="100vh">
        {/* Secondary header bar — matches OrganizationQuestionnaire's sticky bar */}
        <Flex position="sticky" top={0} zIndex={10} bg="white" h="52px" flexShrink={0} align="center" justify="flex-end" />

        {/* Page content */}
        <Grid templateColumns="1fr" bg="pureWhite" flex={1}>
          <Stack spacing={3} pt={2} pb={4} px={4} mx="10%" minW={0}>
            {/* Back link */}
            <Flex align="center">
              <Button
                variant="link"
                color="limeShade.600"
                leftIcon={<FaArrowLeft />}
                onClick={handleReturnToListClick}
                fontSize="16px"
                fontWeight="600"
                _hover={{ textDecoration: "none" }}
              >
                <Text as="span" color="whiteShade.600" fontSize="16px" fontWeight="600">
                  Tasks
                </Text>
              </Button>
            </Flex>

            {/* Heading */}
            <Flex justify="space-between" align="flex-start" gap={4}>
              <Heading fontSize="24px" fontWeight="700" color="darkTeal" mb={2}>
                {questionnaireData.description || t(`${translationPath}.title`)}
              </Heading>
              {showGetBackButton && (
                <Button
                  variant="outline"
                  borderRadius="full"
                  borderColor="limeShade.400"
                  color="limeShade.700"
                  fontWeight="600"
                  _hover={{ bg: "lightLime", borderColor: "limeShade.500" }}
                  flexShrink={0}
                  title={t("reportsPage.getBackBtnTooltip")}
                  onClick={() => onGetBackDialogOpen()}
                >
                  {t("reportsPage.getBackBtn")}
                </Button>
              )}
            </Flex>

            {/* Period + assignee meta row */}
            <HStack spacing={4} mb={3} flexWrap="wrap">
              {questionnaireData.period?.year && (
                <HStack spacing={1.5}>
                  <CalendarBlank size={13} color="#A0AEC0" />
                  <Text fontSize="13px" color="gray.500">
                    {questionnaireData.period.year}
                    {questionnaireData.period.quarter ? ` ${questionnaireData.period.quarter}` : ""}
                  </Text>
                </HStack>
              )}
              {(questionnaireData.assigneeName || assignee?.name) && (
                <Tag size="sm" bg="#FFF8E1" borderRadius="md" px={2} gap={1.5}>
                  <User size={12} color="#718096" />
                  <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">
                    {questionnaireData.assigneeName || assignee?.name}
                  </Text>
                </Tag>
              )}
            </HStack>

            <Divider borderColor="gray.200" />

            {categoryComponent}
            <ConfirmationDialog
              isOpen={isGetBackDialogOpen}
              onClose={() => onGetBackDialogClose()}
              onConfirm={handleCategorySwitch}
              headerText={t("reportsPage.getBackDialogHeader")}
              confirmationText={t("reportsPage.getBackQuestion")}
            />
          </Stack>
        </Grid>
      </Flex>
    );
  };

  if ((error && error.response.status === 404) || (!isLoading && !isFetching && typeof data === "undefined")) {
    return (
      <Flex blockSize="full">
        <Center flex="1">
          <Box textAlign="center">
            {error && error.response.status === 404 ? (
              <>
                <Tag size="lg">{t("noAvailableQuestionnaire")}</Tag>
                <Box mt={3}>
                  <Button
                    variant="ghost"
                    leftIcon={<CaretLeft />}
                    onClick={() => navigate(getSupplierQuestionnairesRoute(userPermissions))}
                    fontSize="sm"
                    color="gray.700"
                  >
                    Back to tasks
                  </Button>
                </Box>
              </>
            ) : (
              <Tag size="lg">{t("somethingWentWrong")}</Tag>
            )}
          </Box>
        </Center>
      </Flex>
    );
  } else {
    return questionnaireData && typeof questionnaireData !== "undefined" ? renderCategoryComponent() : null;
  }
}

export default WithAuthorization(SupplierQuestionnaire);
